import React, { useState, useEffect } from 'react';
import { orderAPI, adminAPI } from '../../api';
import { Search, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS = {
  pending: { bg: '#fef3c7', text: '#d97706' },
  confirmed: { bg: '#dbeafe', text: '#2563eb' },
  shipped: { bg: '#ede9fe', text: '#7c3aed' },
  delivered: { bg: '#dcfce7', text: '#16a34a' },
  cancelled: { bg: '#fee2e2', text: '#dc2626' },
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [updating, setUpdating] = useState(null);
  const limit = 12;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getOrders({ page, limit, status: statusFilter, search });
      setOrders(res.data.orders || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [page, statusFilter, search]);

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await orderAPI.updateStatus(orderId, newStatus);
      toast.success('Status updated');
      fetchOrders();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Orders</h1>
        <p style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>{total} orders total</p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, background: 'white', borderRadius: 12, padding: '10px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Search size={16} color="#94a3b8" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by order ID or customer..." style={{ border: 'none', outline: 'none', fontSize: 14, flex: 1 }} />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 16px', fontSize: 14, outline: 'none', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {['', ...STATUSES].map(s => {
          const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All';
          const active = statusFilter === s;
          return (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{ padding: '6px 16px', borderRadius: 20, border: 'none', background: active ? '#f43397' : '#f1f5f9', color: active ? 'white' : '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Order ID', 'Customer', 'Items', 'Amount', 'Payment', 'Date', 'Status', 'Action'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No orders found</td></tr>
                ) : orders.map(order => {
                  const sc = STATUS_COLORS[order.status] || { bg: '#f1f5f9', text: '#64748b' };
                  return (
                    <tr key={order.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#6c3fc5', fontWeight: 600 }}>
                        #{order.id?.slice(0, 8).toUpperCase()}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{order.customer_name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{order.customer_email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{order.item_count || 1} items</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>₹{parseFloat(order.total_amount || 0).toFixed(0)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: order.payment_method === 'cod' ? '#fef3c7' : '#dcfce7', color: order.payment_method === 'cod' ? '#d97706' : '#16a34a', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
                          {order.payment_method || 'cod'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8' }}>
                        {new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: sc.bg, color: sc.text, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{order.status}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <select
                          value={order.status}
                          onChange={e => handleStatusChange(order.id, e.target.value)}
                          disabled={updating === order.id || order.status === 'delivered' || order.status === 'cancelled'}
                          style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: 'white', cursor: 'pointer', outline: 'none' }}
                        >
                          {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 6, justifyContent: 'center' }}>
            {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: page === p ? '#f43397' : '#f1f5f9', color: page === p ? 'white' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
